import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { vendorAPI, rfqAPI, quotationAPI, poAPI, invoiceAPI, uploadFile, openUploadedFile } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDate } from '../utils/formatDate';
import { maskPAN, maskGSTIN, maskBankAccount, maskAadhaar } from '../utils/validations';
import {
  Building2, FileText, Clock, CheckCircle2, XCircle, AlertCircle,
  MessageSquare, LogOut, Edit, Send, Loader2, ShoppingCart, Receipt,
  FileCheck, Package, Eye, Plus, EyeOff
} from 'lucide-react';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: Edit },
  submitted: { label: 'Submitted', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Clock },
  under_review: { label: 'Under Review', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  query_raised: { label: 'Query Raised', color: 'bg-purple-50 text-purple-700 border-purple-200', icon: MessageSquare },
  approved: { label: 'Approved', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
};

const PO_STATUS = {
  issued: { label: 'Issued', color: 'bg-blue-100 text-blue-700' },
  acknowledged: { label: 'Acknowledged', color: 'bg-purple-100 text-purple-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
};

const INVOICE_STATUS = {
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700' },
  verified: { label: 'Verified', color: 'bg-purple-100 text-purple-700' },
  payment_initiated: { label: 'Payment Initiated', color: 'bg-amber-100 text-amber-700' },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-700' },
};

export default function VendorDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [application, setApplication] = useState(null);
  const [queries, setQueries] = useState([]);
  const [rfqs, setRFQs] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [pos, setPOs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  
  // Modal states
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Quotation form
  const [quotationForm, setQuotationForm] = useState({
    items: [],
    total_amount: 0,
    validity_days: 30,
    delivery_terms: '',
    payment_terms: '',
    remarks: ''
  });

  // Invoice form
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    amount: 0,
    tax_amount: 0,
    total_amount: 0,
    remarks: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appResponse, queriesResponse] = await Promise.all([
        vendorAPI.getApplication(),
        vendorAPI.getQueries(),
      ]);
      setApplication(appResponse.data);
      setQueries(queriesResponse.data || []);

      // If vendor is approved, fetch RFQs, POs, Invoices
      if (appResponse.data?.status === 'approved') {
        const [rfqRes, quotRes, poRes, invRes] = await Promise.all([
          rfqAPI.getRFQs('open'),
          quotationAPI.getMyQuotations(),
          poAPI.getPOs(),
          invoiceAPI.getInvoices(),
        ]);
        setRFQs(rfqRes.data || []);
        setQuotations(quotRes.data || []);
        setPOs(poRes.data || []);
        setInvoices(invRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleRespondToQuery = async (queryId) => {
    if (!responseText.trim()) {
      toast.error('Please enter a response');
      return;
    }

    setResponding(true);
    try {
      await vendorAPI.respondToQuery(queryId, {
        response_text: responseText,
        attachments: [],
      });
      toast.success('Response submitted successfully');
      setResponseText('');
      setSelectedQuery(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit response');
    } finally {
      setResponding(false);
    }
  };

  const openQuotationModal = (rfq) => {
    setSelectedRFQ(rfq);
    setQuotationForm({
      items: rfq.items.map(item => ({
        item_name: item.item_name,
        quantity: item.quantity,
        unit_price: 0,
        total_price: 0,
        delivery_days: 7,
        remarks: ''
      })),
      total_amount: 0,
      validity_days: 30,
      delivery_terms: '',
      payment_terms: '',
      remarks: ''
    });
    setShowQuotationModal(true);
  };

  const updateQuotationItem = (index, field, value) => {
    const newItems = [...quotationForm.items];
    newItems[index][field] = value;
    if (field === 'unit_price') {
      newItems[index].total_price = value * newItems[index].quantity;
    }
    const total = newItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
    setQuotationForm({ ...quotationForm, items: newItems, total_amount: total });
  };

  const submitQuotation = async () => {
    if (quotationForm.total_amount <= 0) {
      toast.error('Please enter valid prices');
      return;
    }

    setActionLoading(true);
    try {
      await quotationAPI.submitQuotation({
        rfq_id: selectedRFQ.id,
        items: quotationForm.items,
        total_amount: quotationForm.total_amount,
        validity_days: quotationForm.validity_days,
        delivery_terms: quotationForm.delivery_terms,
        payment_terms: quotationForm.payment_terms,
        remarks: quotationForm.remarks
      });
      toast.success('Quotation submitted successfully');
      setShowQuotationModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quotation');
    } finally {
      setActionLoading(false);
    }
  };

  const acknowledgePO = async (poId) => {
    try {
      await poAPI.acknowledgePO(poId, { remarks: '' });
      toast.success('PO acknowledged');
      fetchData();
    } catch (error) {
      toast.error('Failed to acknowledge PO');
    }
  };

  const openInvoiceModal = (po) => {
    setSelectedPO(po);
    setInvoiceForm({
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      amount: po.total_amount,
      tax_amount: Math.round(po.total_amount * 0.18), // 18% GST
      total_amount: Math.round(po.total_amount * 1.18),
      remarks: ''
    });
    setShowInvoiceModal(true);
  };

  const submitInvoice = async () => {
    if (!invoiceForm.invoice_number) {
      toast.error('Please enter invoice number');
      return;
    }

    setActionLoading(true);
    try {
      await invoiceAPI.submitInvoice({
        po_id: selectedPO.id,
        ...invoiceForm
      });
      toast.success('Invoice submitted successfully');
      setShowInvoiceModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#003380]" />
      </div>
    );
  }

  const isApproved = application?.status === 'approved';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="tricolor-strip" />
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/iith-logo.png" 
                alt="IIT Hyderabad" 
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold govt-blue-text font-serif">Vendor Portal</h1>
                <p className="text-sm text-slate-600">Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                Welcome, <span className="font-medium">{user?.name}</span>
              </span>
              <Button variant="outline" onClick={handleLogout} size="sm" data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {!application ? (
          <Card className="govt-card">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Building2 className="h-16 w-16 text-slate-300 mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Application Found</h2>
              <p className="text-slate-600 mb-6">Start your vendor registration to participate in procurement</p>
              <Link to="/vendor/registration">
                <Button className="govt-btn-primary" data-testid="start-registration-btn">
                  Start Registration
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Application Status Card */}
            <Card className="govt-card mb-6">
              <CardHeader className="govt-card-header">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-[#003380]" />
                      Application: {application.application_number}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {application.basic_info?.company_name || 'Company Name Not Set'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(application.status)}
                    {application.status === 'draft' && (
                      <Link to="/vendor/registration">
                        <Button className="govt-btn-primary" size="sm" data-testid="continue-registration-btn">
                          <Edit className="h-4 w-4 mr-2" />
                          Continue Registration
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Business Type</p>
                    <p className="font-medium capitalize">{application.basic_info?.business_type?.replace('_', ' ') || '-'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">PAN Number</p>
                    <p className="font-medium">{application.basic_info?.pan_number || '-'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Submitted On</p>
                    <p className="font-medium">
                      {formatDate(application.submitted_at)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Categories</p>
                    <p className="font-medium capitalize">
                      {application.capability?.categories?.join(', ') || '-'}
                    </p>
                  </div>
                </div>

                {application.status === 'approved' && (
                  <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-emerald-700">Vendor Registration Approved</p>
                        <p className="text-sm text-emerald-600">
                          Category: {application.approved_category?.replace('_', ' ')} | 
                          Validity: {application.validity_years} Year(s)
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {application.status === 'rejected' && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-700">Application Rejected</p>
                        <p className="text-sm text-red-600">Reason: {application.rejection_reason}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Main Content Tabs */}
            <Tabs defaultValue={isApproved ? "rfqs" : "queries"} className="space-y-4">
              <TabsList className="bg-white border">
                {isApproved && (
                  <>
                    <TabsTrigger value="rfqs" data-testid="rfqs-tab">
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Open RFQs
                      {rfqs.length > 0 && (
                        <Badge className="ml-2 bg-blue-500 text-white">{rfqs.length}</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="quotations" data-testid="quotations-tab">
                      <FileCheck className="h-4 w-4 mr-2" />
                      My Quotations
                    </TabsTrigger>
                    <TabsTrigger value="pos" data-testid="pos-tab">
                      <Package className="h-4 w-4 mr-2" />
                      Purchase Orders
                      {pos.filter(p => p.status === 'issued').length > 0 && (
                        <Badge className="ml-2 bg-amber-500 text-white">
                          {pos.filter(p => p.status === 'issued').length}
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="invoices" data-testid="invoices-tab">
                      <Receipt className="h-4 w-4 mr-2" />
                      Invoices
                    </TabsTrigger>
                  </>
                )}
                <TabsTrigger value="queries" data-testid="queries-tab">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Queries
                  {queries.filter(q => q.status === 'pending').length > 0 && (
                    <Badge className="ml-2 bg-red-500 text-white">
                      {queries.filter(q => q.status === 'pending').length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="documents" data-testid="documents-tab">
                  <FileText className="h-4 w-4 mr-2" />
                  Documents
                </TabsTrigger>
              </TabsList>

              {/* RFQs Tab */}
              {isApproved && (
                <TabsContent value="rfqs">
                  <Card className="govt-card">
                    <CardHeader className="govt-card-header">
                      <CardTitle className="text-lg">Open Requests for Quotation</CardTitle>
                      <CardDescription>Submit your quotations against open RFQs</CardDescription>
                    </CardHeader>
                    <CardContent className="p-6">
                      {rfqs.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                          <p>No open RFQs available</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {rfqs.map((rfq) => (
                            <div key={rfq.id} className="border rounded-lg p-4 hover:bg-slate-50">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-semibold">{rfq.title}</h4>
                                  <p className="text-sm text-slate-600">{rfq.rfq_number}</p>
                                  <p className="text-sm text-slate-500 mt-1">{rfq.description}</p>
                                  <div className="flex items-center gap-4 mt-2 text-sm">
                                    <span className="text-slate-500">
                                      Category: <Badge variant="outline" className="capitalize">{rfq.category}</Badge>
                                    </span>
                                    <span className="text-slate-500">
                                      Items: {rfq.items?.length || 0}
                                    </span>
                                    <span className="text-red-600">
                                      Deadline: {formatDate(rfq.submission_deadline)}
                                    </span>
                                  </div>
                                </div>
                                <Button onClick={() => openQuotationModal(rfq)} data-testid={`submit-quote-${rfq.id}`}>
                                  <Plus className="h-4 w-4 mr-2" />
                                  Submit Quotation
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              {/* Quotations Tab */}
              {isApproved && (
                <TabsContent value="quotations">
                  <Card className="govt-card">
                    <CardHeader className="govt-card-header">
                      <CardTitle className="text-lg">My Quotations</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      {quotations.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <FileCheck className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                          <p>No quotations submitted yet</p>
                        </div>
                      ) : (
                        <table className="govt-table">
                          <thead>
                            <tr>
                              <th>Quotation #</th>
                              <th>RFQ</th>
                              <th>Amount</th>
                              <th>Submitted</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quotations.map((q) => (
                              <tr key={q.id}>
                                <td className="font-medium">{q.quotation_number}</td>
                                <td>{q.rfq_title}</td>
                                <td>₹{q.total_amount?.toLocaleString('en-IN')}</td>
                                <td>{formatDate(q.submitted_at)}</td>
                                <td>
                                  <Badge className={
                                    q.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                                    q.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                    'bg-blue-100 text-blue-700'
                                  }>
                                    {q.status?.replace('_', ' ')}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              {/* Purchase Orders Tab */}
              {isApproved && (
                <TabsContent value="pos">
                  <Card className="govt-card">
                    <CardHeader className="govt-card-header">
                      <CardTitle className="text-lg">Purchase Orders</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      {pos.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                          <p>No purchase orders yet</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {pos.map((po) => (
                            <div key={po.id} className="border rounded-lg p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h4 className="font-semibold">{po.po_number}</h4>
                                  <p className="text-sm text-slate-500">
                                    Amount: ₹{po.total_amount?.toLocaleString('en-IN')}
                                  </p>
                                </div>
                                <Badge className={PO_STATUS[po.status]?.color || 'bg-slate-100'}>
                                  {PO_STATUS[po.status]?.label || po.status}
                                </Badge>
                              </div>
                              <div className="grid md:grid-cols-3 gap-4 text-sm mb-3">
                                <div>
                                  <span className="text-slate-500">Delivery Date:</span>
                                  <span className="ml-2">{formatDate(po.delivery_date)}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Delivery Status:</span>
                                  <span className="ml-2 capitalize">{po.delivery_status || 'Pending'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Payment:</span>
                                  <span className="ml-2 capitalize">{po.payment_status || 'Pending'}</span>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                {po.status === 'issued' && (
                                  <Button size="sm" onClick={() => acknowledgePO(po.id)} data-testid={`ack-po-${po.id}`}>
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Acknowledge PO
                                  </Button>
                                )}
                                {['acknowledged', 'in_progress', 'delivered'].includes(po.status) && (
                                  <Button size="sm" variant="outline" onClick={() => openInvoiceModal(po)} data-testid={`submit-invoice-${po.id}`}>
                                    <Receipt className="h-4 w-4 mr-2" />
                                    Submit Invoice
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              {/* Invoices Tab */}
              {isApproved && (
                <TabsContent value="invoices">
                  <Card className="govt-card">
                    <CardHeader className="govt-card-header">
                      <CardTitle className="text-lg">Invoices</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      {invoices.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                          <p>No invoices submitted yet</p>
                        </div>
                      ) : (
                        <table className="govt-table">
                          <thead>
                            <tr>
                              <th>Invoice #</th>
                              <th>PO #</th>
                              <th>Date</th>
                              <th>Amount</th>
                              <th>Status</th>
                              <th>Payment</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoices.map((inv) => (
                              <tr key={inv.id}>
                                <td className="font-medium">{inv.invoice_number}</td>
                                <td>{inv.po_number}</td>
                                <td>{formatDate(inv.invoice_date)}</td>
                                <td>₹{inv.total_amount?.toLocaleString('en-IN')}</td>
                                <td>
                                  <Badge className={INVOICE_STATUS[inv.status]?.color || 'bg-slate-100'}>
                                    {INVOICE_STATUS[inv.status]?.label || inv.status}
                                  </Badge>
                                </td>
                                <td>
                                  {formatDate(inv.payment_date)}
                                  {inv.payment_reference && <span className="text-xs text-slate-500 block">{inv.payment_reference}</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              {/* Queries Tab */}
              <TabsContent value="queries">
                <Card className="govt-card">
                  <CardHeader className="govt-card-header">
                    <CardTitle className="text-lg">Queries & Clarifications</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {queries.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        <p>No queries raised on your application</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {queries.map((query) => (
                          <div 
                            key={query.id} 
                            className={`border rounded-lg p-4 ${
                              query.status === 'pending' ? 'border-amber-300 bg-amber-50' : 'border-slate-200'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <Badge className={query.status === 'pending' ? 'bg-amber-500' : 'bg-emerald-500'}>
                                {query.status === 'pending' ? 'Response Required' : 'Responded'}
                              </Badge>
                              <span className="text-xs text-slate-500">
                                Deadline: {formatDate(query.response_deadline)}
                              </span>
                            </div>
                            
                            <div className="bg-white rounded p-3 mb-3">
                              <p className="text-sm font-medium text-slate-700 mb-1">Sections: {query.sections?.join(', ')}</p>
                              <p className="text-sm text-slate-600">{query.questions}</p>
                            </div>

                            {query.response_text && (
                              <div className="bg-emerald-50 rounded p-3 mb-3">
                                <p className="text-sm font-medium text-emerald-700 mb-1">Your Response:</p>
                                <p className="text-sm text-emerald-600">{query.response_text}</p>
                              </div>
                            )}

                            {query.status === 'pending' && (
                              <div className="mt-3">
                                {selectedQuery === query.id ? (
                                  <div className="space-y-3">
                                    <Textarea
                                      placeholder="Enter your response..."
                                      value={responseText}
                                      onChange={(e) => setResponseText(e.target.value)}
                                      rows={3}
                                    />
                                    <div className="flex gap-2">
                                      <Button onClick={() => handleRespondToQuery(query.id)} disabled={responding} className="govt-btn-primary">
                                        {responding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                                        Submit Response
                                      </Button>
                                      <Button variant="outline" onClick={() => { setSelectedQuery(null); setResponseText(''); }}>
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <Button variant="outline" onClick={() => setSelectedQuery(query.id)}>
                                    <MessageSquare className="h-4 w-4 mr-2" />
                                    Respond to Query
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents">
                <Card className="govt-card">
                  <CardHeader className="govt-card-header">
                    <CardTitle className="text-lg">Uploaded Documents</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {application.compliance_documents?.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        <p>No documents uploaded yet</p>
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-4">
                        {application.compliance_documents?.map((doc, idx) => (
                          <div key={idx} className="border border-slate-200 rounded-lg p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                                <FileText className="h-5 w-5 text-slate-500" />
                              </div>
                              <div>
                                <p className="font-medium capitalize">{doc.document_type?.replace(/_/g, ' ')}</p>
                                <p className="text-sm text-slate-500">{doc.file_name || 'Document'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {doc.file_path && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      await openUploadedFile(doc.file_path);
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || err.message || 'Unable to open document');
                                    }
                                  }}
                                  data-testid={`view-doc-${idx}-btn`}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                              )}
                              <Badge className={doc.verified ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'}>
                                {doc.verified ? 'Verified' : 'Pending'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      {/* Quotation Modal */}
      <Dialog open={showQuotationModal} onOpenChange={setShowQuotationModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submit Quotation</DialogTitle>
            <DialogDescription>
              {selectedRFQ?.title} ({selectedRFQ?.rfq_number})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2">Item</th>
                    <th className="text-left p-2">Qty</th>
                    <th className="text-left p-2">Unit Price (₹)</th>
                    <th className="text-left p-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {quotationForm.items.map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-2">{item.item_name}</td>
                      <td className="p-2">{item.quantity}</td>
                      <td className="p-2">
                        <Input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateQuotationItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="w-24"
                        />
                      </td>
                      <td className="p-2">₹{item.total_price?.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-semibold">
                  <tr>
                    <td colSpan={3} className="p-2 text-right">Total Amount:</td>
                    <td className="p-2">₹{quotationForm.total_amount?.toLocaleString('en-IN')}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Validity (Days)</Label>
                <Input
                  type="number"
                  value={quotationForm.validity_days}
                  onChange={(e) => setQuotationForm({ ...quotationForm, validity_days: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Delivery Terms</Label>
                <Input
                  value={quotationForm.delivery_terms}
                  onChange={(e) => setQuotationForm({ ...quotationForm, delivery_terms: e.target.value })}
                  placeholder="e.g., Ex-works, FOB"
                />
              </div>
            </div>

            <div>
              <Label>Payment Terms</Label>
              <Input
                value={quotationForm.payment_terms}
                onChange={(e) => setQuotationForm({ ...quotationForm, payment_terms: e.target.value })}
                placeholder="e.g., 30 days from delivery"
              />
            </div>

            <div>
              <Label>Remarks</Label>
              <Textarea
                value={quotationForm.remarks}
                onChange={(e) => setQuotationForm({ ...quotationForm, remarks: e.target.value })}
                placeholder="Any additional remarks..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuotationModal(false)}>Cancel</Button>
            <Button onClick={submitQuotation} disabled={actionLoading} className="govt-btn-primary">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Quotation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Invoice</DialogTitle>
            <DialogDescription>
              Against PO: {selectedPO?.po_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Invoice Number *</Label>
                <Input
                  value={invoiceForm.invoice_number}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })}
                  placeholder="INV-2024-001"
                />
              </div>
              <div>
                <Label>Invoice Date</Label>
                <Input
                  type="date"
                  value={invoiceForm.invoice_date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Amount (₹)</Label>
                <Input
                  type="number"
                  value={invoiceForm.amount}
                  onChange={(e) => {
                    const amt = parseFloat(e.target.value) || 0;
                    const tax = Math.round(amt * 0.18);
                    setInvoiceForm({ ...invoiceForm, amount: amt, tax_amount: tax, total_amount: amt + tax });
                  }}
                />
              </div>
              <div>
                <Label>Tax (₹)</Label>
                <Input
                  type="number"
                  value={invoiceForm.tax_amount}
                  onChange={(e) => {
                    const tax = parseFloat(e.target.value) || 0;
                    setInvoiceForm({ ...invoiceForm, tax_amount: tax, total_amount: invoiceForm.amount + tax });
                  }}
                />
              </div>
              <div>
                <Label>Total (₹)</Label>
                <Input type="number" value={invoiceForm.total_amount} disabled className="bg-slate-50" />
              </div>
            </div>

            <div>
              <Label>Remarks</Label>
              <Textarea
                value={invoiceForm.remarks}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, remarks: e.target.value })}
                placeholder="Any additional remarks..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>Cancel</Button>
            <Button onClick={submitInvoice} disabled={actionLoading} className="govt-btn-primary">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
